import Cookies from 'js-cookie';
import experimentsConfig from '../experiments/experiments.json';

const COOKIE_NAME = 'cocopool_ab';
const COOKIE_MAX_AGE_DAYS = 180;

export const parseAssignments = cookieValue => {
  if (!cookieValue) return {};
  try {
    const parsed = JSON.parse(cookieValue);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

export const serializeAssignments = assignments => JSON.stringify(assignments);

const pickVariant = variants => {
  const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);
  let rand = Math.random() * totalWeight;
  for (const variant of variants) {
    rand -= variant.weight;
    if (rand <= 0) return variant.name;
  }
  return variants[variants.length - 1].name;
};

/**
 * For each active experiment, returns the existing assignment or picks a new one.
 * Inactive experiments always return the first variant (control).
 */
export const resolveExperiments = (config, existingAssignments = {}) => {
  const result = {};
  for (const experiment of config) {
    if (!experiment.active) {
      result[experiment.name] = experiment.variants[0].name;
    } else if (
      existingAssignments[experiment.name] &&
      experiment.variants.some(v => v.name === existingAssignments[experiment.name])
    ) {
      result[experiment.name] = existingAssignments[experiment.name];
    } else {
      result[experiment.name] = pickVariant(experiment.variants);
    }
  }
  return result;
};

/**
 * Reconcile cookie-side assignments against the user's stored assignments.
 * The user's metadata is the source of truth: for each active experiment, if the user already
 * has a valid variant we adopt it (overriding the cookie). Otherwise the cookie value is kept,
 * or a fresh one is picked as a last resort. Inactive experiments are forced to control.
 */
export const reconcileWithUser = (config, cookieAssignments = {}, userAssignments = {}) => {
  const result = {};
  for (const experiment of config) {
    if (!experiment.active) {
      result[experiment.name] = experiment.variants[0].name;
      continue;
    }
    const userVariant = userAssignments[experiment.name];
    const userVariantValid = userVariant && experiment.variants.some(v => v.name === userVariant);
    if (userVariantValid) {
      result[experiment.name] = userVariant;
      continue;
    }
    const cookieVariant = cookieAssignments[experiment.name];
    const cookieVariantValid =
      cookieVariant && experiment.variants.some(v => v.name === cookieVariant);
    result[experiment.name] = cookieVariantValid ? cookieVariant : pickVariant(experiment.variants);
  }
  return result;
};

export const assignmentsEqual = (a = {}, b = {}) => {
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every(k => a[k] === b[k]);
};

export const getVariant = (assignments, experimentName) => {
  return assignments?.[experimentName] || null;
};

export const readAssignmentsFromCookie = () => {
  const raw = Cookies.get(COOKIE_NAME);
  return parseAssignments(raw);
};

export const writeAssignmentsToCookie = assignments => {
  Cookies.set(COOKIE_NAME, serializeAssignments(assignments), {
    expires: COOKIE_MAX_AGE_DAYS,
    path: '/',
    sameSite: 'lax',
  });
};

/**
 * Resolve experiments and persist to cookie. Typically called on client-side init
 * when SSR hasn't already resolved (e.g. client-only navigation).
 */
export const initExperimentsClient = () => {
  const existing = readAssignmentsFromCookie();
  const assignments = resolveExperiments(experimentsConfig, existing);
  writeAssignmentsToCookie(assignments);
  return assignments;
};

export { experimentsConfig };
